import { BadgeCheck, CalendarDays, Landmark, Mail, Phone } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { admin, getImageUrl } from "../../services/api";

const formatDate = (value, options) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleString(undefined, options);
};

const ClaimsManagementPanel = ({ compact = false }) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedClaim, setSelectedClaim] = useState(null);

  useEffect(() => {
    const fetchClaims = async () => {
      try {
        const res = await admin.getClaims(filter);
        setClaims(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching claims:", err);
        toast.error("Failed to load claims");
      } finally {
        setLoading(false);
      }
    };

    fetchClaims();
  }, [filter]);

  const visibleClaims = useMemo(
    () => (compact ? claims.slice(0, 4) : claims),
    [claims, compact],
  );

  const handleApproveClaim = async (claimId) => {
    try {
      await admin.approveClaim(claimId);
      setClaims((prev) => prev.filter((claim) => claim._id !== claimId));
      toast.success("Claim approved successfully");
      setSelectedClaim(null);
    } catch (err) {
      console.error("Error approving claim:", err);
      toast.error(err.response?.data?.message || "Failed to approve claim");
    }
  };

  const handleRejectClaim = async (claimId, reason) => {
    try {
      await admin.rejectClaim(claimId, { reason });
      setClaims((prev) => prev.filter((claim) => claim._id !== claimId));
      toast.success("Claim rejected successfully");
      setSelectedClaim(null);
    } catch (err) {
      console.error("Error rejecting claim:", err);
      toast.error(err.response?.data?.message || "Failed to reject claim");
    }
  };

  const statusColors = {
    pending: "bg-[#fff1df] text-[#b76500]",
    approved: "bg-[#edf8ef] text-[#106d06]",
    rejected: "bg-[#fff0ec] text-[#c44c29]",
  };

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#ff9933]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!compact && (
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Claims</h2>
            <p className="text-sm text-slate-500">Candidate verification requests backed by the live admin claims API.</p>
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm focus:border-[#ff9933] focus:outline-none focus:ring-2 focus:ring-[#ffe1b8]"
          >
            <option value="all">All Claims</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      )}

      <div className="grid gap-4">
        {visibleClaims.map((claim, index) => (
          <motion.div
            key={claim._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
            className="rounded-[1.75rem] border border-[#d7dfd4] bg-white p-5 shadow-sm"
          >
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${statusColors[claim.status] || statusColors.pending}`}>
                    {claim.status || "pending"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                    <CalendarDays className="h-3.5 w-3.5" />
                    Submitted {formatDate(claim.submittedAt, { dateStyle: "medium" })}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">{claim.fullName || "Unknown candidate"}</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {claim.position || "Position unavailable"} • {claim.party || "Party unavailable"}
                  </p>
                </div>
                <div className="grid gap-2 text-sm text-slate-600 md:grid-cols-2">
                  <p className="inline-flex items-center gap-2"><Landmark className="h-4 w-4 text-slate-400" /> {claim.constituency || "N/A"}</p>
                  <p>{claim.state || "N/A"}</p>
                  <p className="inline-flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {claim.email || "N/A"}</p>
                  <p className="inline-flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {claim.phone || "N/A"}</p>
                  <p>Claimant: {claim.claimantId?.name || "N/A"}</p>
                  <p>Role Before Approval: {claim.claimantId?.role || "N/A"}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 xl:justify-end">
                {!compact && (
                  <button
                    onClick={() => setSelectedClaim(claim)}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
                  >
                    View Details
                  </button>
                )}
                {!compact && claim.status === "pending" && (
                  <>
                    <button
                      onClick={() => handleApproveClaim(claim._id)}
                      className="rounded-2xl bg-[#138808] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#106d06]"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        const reason = window.prompt("Enter rejection reason:");
                        if (reason) handleRejectClaim(claim._id, reason);
                      }}
                      className="rounded-2xl bg-[#d95d39] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#c44c29]"
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {visibleClaims.length === 0 && (
        <div className="rounded-[1.75rem] border border-dashed border-slate-200 bg-slate-50 p-10 text-center text-slate-500">
          No claims are waiting for review.
        </div>
      )}

      {!compact && selectedClaim && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedClaim(null)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-100 bg-gradient-to-r from-[#fff1df] via-white to-[#edf8ef] px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
                  <BadgeCheck className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{selectedClaim.fullName || "Claim Details"}</h3>
                  <p className="text-sm text-slate-500">{selectedClaim.position || "Position unavailable"}</p>
                </div>
              </div>
            </div>

            <div className="space-y-5 p-6">
              <div className="grid gap-4 text-sm text-slate-700 sm:grid-cols-2">
                <p>Party: {selectedClaim.party || "N/A"}</p>
                <p>State: {selectedClaim.state || "N/A"}</p>
                <p>District: {selectedClaim.district || "N/A"}</p>
                <p>Constituency: {selectedClaim.constituency || "N/A"}</p>
                <p>Email: {selectedClaim.email || "N/A"}</p>
                <p>Phone: {selectedClaim.phone || "N/A"}</p>
                <p>Claimant: {selectedClaim.claimantId?.name || "N/A"}</p>
                <p>Aadhaar Last 4: {selectedClaim.aadhaarLast4 || "N/A"}</p>
                <p>Submitted At: {formatDate(selectedClaim.submittedAt)}</p>
              </div>

              {(selectedClaim.idProof || selectedClaim.selfieWithId || selectedClaim.photo) && (
                <div className="grid gap-4 sm:grid-cols-2">
                  {selectedClaim.idProof && (
                    <div>
                      <h4 className="mb-3 font-semibold text-slate-900">ID Proof</h4>
                      <a
                        href={getImageUrl(selectedClaim.idProof)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-medium text-orange-600 hover:text-orange-700"
                      >
                        Open uploaded document
                      </a>
                    </div>
                  )}
                  {(selectedClaim.selfieWithId || selectedClaim.photo) && (
                    <div>
                      <h4 className="mb-3 font-semibold text-slate-900">Selfie With ID</h4>
                      <img
                        src={getImageUrl(selectedClaim.selfieWithId || selectedClaim.photo)}
                        alt={selectedClaim.fullName || "Claim"}
                        className="h-40 w-40 rounded-3xl object-cover shadow-md"
                      />
                    </div>
                  )}
                </div>
              )}

              {selectedClaim.proofDocuments?.length > 0 && (
                <div>
                  <h4 className="mb-3 font-semibold text-slate-900">Proof Documents</h4>
                  <div className="space-y-2">
                    {selectedClaim.proofDocuments.map((doc, index) => (
                      <div key={index} className="rounded-3xl bg-slate-50 p-4 text-sm text-slate-600">
                        {doc}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedClaim.status === "pending" && (
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={() => handleApproveClaim(selectedClaim._id)}
                    className="flex-1 rounded-2xl bg-[#138808] px-4 py-3 font-medium text-white transition-colors hover:bg-[#106d06]"
                  >
                    Approve Claim
                  </button>
                  <button
                    onClick={() => {
                      const reason = window.prompt("Enter rejection reason:");
                      if (reason) handleRejectClaim(selectedClaim._id, reason);
                    }}
                    className="flex-1 rounded-2xl bg-[#d95d39] px-4 py-3 font-medium text-white transition-colors hover:bg-[#c44c29]"
                  >
                    Reject Claim
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default ClaimsManagementPanel;
